import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNetworkStatus } from '../useNetworkStatus';
import { useFindGroups } from './useFindGroups';
import { useFestivalSets } from './useFestivalSets';
import { useMyRsvps } from './useMyRsvps';
import { useCurrentUserProfile } from '../../contexts/CurrentUserProfileContext';
import { downloadGroup, downloadSets, isGroupDue } from '../../lib/eventDayOffline';
import { isInDownloadWindow } from '../../lib/festivalOffline';
import {
  onNotificationRow,
  MEMBERSHIP_NOTIFICATION_TYPES,
} from '../../lib/realtime/notificationEvents';

const AGAIN_AFTER_MS = 10 * 60 * 1000; // with signal, an event-day refresh at most this often per group or festival
const lastAt = new Map();

/**
 * Your groups and Sets on the phone before the day (lib/eventDayOffline): from three days before an event you are in a
 * group for, or a festival you are going to, until it is over - map or not. On a reachable network, one at a time,
 * again on coming back to the app; offline, every request fails fast and what is saved stays.
 */
export function usePrefetchEventDay(userId) {
  const queryClient = useQueryClient();
  const { authReady } = useCurrentUserProfile();
  const { prefetchAllowed } = useNetworkStatus();
  const { data: groups } = useFindGroups(userId, { authReady });
  const { data: festivals } = useFestivalSets();
  const { data: rsvps } = useMyRsvps(userId, { enabled: authReady });
  const [resumeTick, setResumeTick] = useState(0);
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) setResumeTick((n) => n + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    // A membership change in one of your groups (someone joined, you were approved): save that group again now, not after
    // its 10 min gap, so the copy on the phone has the new member.
    const stop = onNotificationRow((row) => {
      if (!row?.related_group_id || !MEMBERSHIP_NOTIFICATION_TYPES.has(row.type)) return;
      lastAt.delete(`g:${row.related_group_id}`);
      setResumeTick((n) => n + 1);
    });
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, []);

  useEffect(() => {
    if (!userId || !prefetchAllowed) return undefined;
    const now = Date.now();
    const going = new Set((rsvps ?? []).filter((r) => r.status === 'going').map((r) => r.event_id));
    const fresh = (key) => now - (lastAt.get(key) ?? 0) < AGAIN_AFTER_MS;
    const dueGroups = (groups ?? []).filter((g) => isGroupDue(g, now) && !fresh(`g:${g.id}`));
    const groupEvents = new Set(dueGroups.map((g) => g.eventId));
    const dueSets = (festivals ?? []).filter(
      (f) =>
        going.has(f.event_id) &&
        !groupEvents.has(f.event_id) &&
        isInDownloadWindow(f, now) &&
        !fresh(`s:${f.event_id}`),
    );
    if (!dueGroups.length && !dueSets.length) return undefined;

    let cancelled = false;
    (async () => {
      for (const g of dueGroups) {
        if (cancelled) return;
        if (await downloadGroup(queryClient, g, userId).catch(() => false))
          lastAt.set(`g:${g.id}`, Date.now());
      }
      for (const f of dueSets) {
        if (cancelled) return;
        if (await downloadSets(queryClient, f.event_id, userId).catch(() => false))
          lastAt.set(`s:${f.event_id}`, Date.now());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, prefetchAllowed, groups, festivals, rsvps, queryClient, resumeTick]);
}
